import React, { useEffect, useState } from 'react';
import { deliveryAPI, DeliveryAddress } from '../../services/deliveryAPI';

// A delivery subscriber's address lives on the subscription, and the Bridge Club
// profile reads it from there - so it is changed HERE, not in the profile's
// Location fields. The server cleans it, re-checks the service ZIPs and re-geocodes;
// this component only shows what the server says.
interface Props {
  onChanged: () => void;
}

const DeliveryAddressSection: React.FC<Props> = ({ onChanged }) => {
  const [address, setAddress] = useState<DeliveryAddress | null>(null);
  const [editing, setEditing] = useState(false);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [message, setMessage] = useState<string | null>(null);
  const [form, setForm] = useState({ service_address: '', city: '', state: '', postal_code: '' });

  useEffect(() => {
    deliveryAPI.getAddress().then(setAddress).catch(() => setAddress(null));
  }, []);

  if (!address) return null;

  const startEditing = () => {
    setForm({
      service_address: address.service_address,
      city: address.city,
      state: address.state,
      postal_code: address.postal_code,
    });
    setError(null);
    setMessage(null);
    setEditing(true);
  };

  const save = async (e: React.FormEvent) => {
    e.preventDefault();
    setSaving(true);
    setError(null);
    try {
      const result = await deliveryAPI.updateAddress(form);
      setAddress(result);
      setMessage(result.message);
      setEditing(false);
      onChanged();
    } catch (err: any) {
      setError(err?.error || err?.detail || "Your address couldn't be saved. Please try again.");
    } finally {
      setSaving(false);
    }
  };

  const set = (key: keyof typeof form) => (e: React.ChangeEvent<HTMLInputElement>) =>
    setForm({ ...form, [key]: e.target.value });

  return (
    <div className="profile-card delivery-address-card">
      <h3 className="delivery-address-title">Delivery address</h3>
      {message && <div className="success-message">{message}</div>}

      {editing ? (
        <form onSubmit={save} className="edit-form">
          {error && <div className="error-message">{error}</div>}
          <div className="form-group">
            <label htmlFor="delivery_street">Street address</label>
            <input id="delivery_street" type="text" autoComplete="address-line1"
              value={form.service_address} onChange={set('service_address')} required />
          </div>
          <div className="form-group">
            <label htmlFor="delivery_city">City</label>
            <input id="delivery_city" type="text" autoComplete="address-level2"
              value={form.city} onChange={set('city')} required />
          </div>
          <div className="form-row">
            <div className="form-group">
              <label htmlFor="delivery_state">State</label>
              <input id="delivery_state" type="text" autoComplete="address-level1" maxLength={2}
                value={form.state} onChange={set('state')} required />
            </div>
            <div className="form-group">
              <label htmlFor="delivery_zip">ZIP Code</label>
              <input id="delivery_zip" type="text" inputMode="numeric" autoComplete="postal-code"
                value={form.postal_code} onChange={set('postal_code')} required />
            </div>
          </div>
          <div className="form-actions">
            <button type="button" onClick={() => setEditing(false)} className="btn-secondary">
              Cancel
            </button>
            <button type="submit" disabled={saving} className="btn-primary">
              {saving ? 'Saving...' : 'Save address'}
            </button>
          </div>
        </form>
      ) : (
        <>
          <div className="profile-details">
            <div className="detail-row">
              <span className="detail-label">Address</span>
              <span className="detail-value">
                {address.service_address}<br />
                {address.city}, {address.state} {address.postal_code}
              </span>
            </div>
          </div>
          {address.needs_verification && (
            <p className="delivery-address-note">
              We'll confirm this address on the map before your next delivery.
            </p>
          )}
          <p className="delivery-address-note">
            Your weather and local info use this address too.
          </p>
          {address.can_change ? (
            <button onClick={startEditing} className="edit-btn">Change delivery address</button>
          ) : (
            <p className="delivery-address-note">{address.change_blocked_reason}</p>
          )}
        </>
      )}
    </div>
  );
};

export default DeliveryAddressSection;
